import { useEffect, useRef, useState } from "react";
import {
  type CanvasAwarenessActiveWidget,
  type CanvasAwarenessFields,
  type CanvasAwarenessUser,
} from "@playbookai/types";

const ACTIVE_WIDGET_MODES: ReadonlySet<CanvasAwarenessActiveWidget["mode"]> =
  new Set(["selected", "editing", "dragging"]);
export const FALLBACK_AWARENESS_COLOR = "hsl(var(--pb-cyan))";

export interface CanvasAwarenessHandle {
  readonly clientID?: number | undefined;
  getStates: () => ReadonlyMap<number, unknown>;
  on: (event: "change", cb: () => void) => void;
  off: (event: "change", cb: () => void) => void;
  setLocalStateField: <K extends "cursor" | "selection" | "activeWidget">(
    key: K,
    value: CanvasAwarenessFields[K],
  ) => void;
}

export interface CanvasAwarenessPeer {
  readonly clientId: number;
  readonly state: CanvasAwarenessFields;
}

export function useCanvasAwarenessPeers(
  awareness: CanvasAwarenessHandle | null | undefined,
): readonly CanvasAwarenessPeer[] {
  const [peers, setPeers] = useState<readonly CanvasAwarenessPeer[]>(() =>
    snapshotCanvasAwareness(awareness),
  );

  useEffect(() => {
    let cancelled = false;
    window.queueMicrotask(() => {
      if (!cancelled) setPeers(snapshotCanvasAwareness(awareness));
    });
    if (!awareness) {
      return (): void => {
        cancelled = true;
      };
    }
    const onChange = (): void => setPeers(snapshotCanvasAwareness(awareness));
    awareness.on("change", onChange);
    return (): void => {
      cancelled = true;
      awareness.off("change", onChange);
    };
  }, [awareness]);

  return peers;
}

export function useCanvasWidgetActivityPeers(
  awareness: CanvasAwarenessHandle | null | undefined,
): readonly CanvasAwarenessPeer[] {
  const [snapshot, setSnapshot] = useState(() =>
    snapshotCanvasWidgetActivity(awareness),
  );
  const signatureRef = useRef(snapshot.signature);

  useEffect(() => {
    let cancelled = false;
    const update = (): void => {
      const nextSnapshot = snapshotCanvasWidgetActivity(awareness);
      if (nextSnapshot.signature === signatureRef.current) return;
      signatureRef.current = nextSnapshot.signature;
      if (!cancelled) setSnapshot(nextSnapshot);
    };

    window.queueMicrotask(update);
    if (!awareness) {
      return (): void => {
        cancelled = true;
      };
    }
    awareness.on("change", update);
    return (): void => {
      cancelled = true;
      awareness.off("change", update);
    };
  }, [awareness]);

  return snapshot.peers;
}

export function setCanvasAwarenessField<
  K extends "cursor" | "selection" | "activeWidget",
>(
  awareness: CanvasAwarenessHandle | null | undefined,
  key: K,
  value: CanvasAwarenessFields[K],
): void {
  awareness?.setLocalStateField(key, value);
}

export function remoteCanvasAwarenessPeers(
  awareness: CanvasAwarenessHandle | null | undefined,
  peers: readonly CanvasAwarenessPeer[],
): readonly CanvasAwarenessPeer[] {
  const localId = awareness?.clientID;
  return peers.filter((peer) => peer.clientId !== localId);
}

export function awarenessColor(color: string | undefined): string {
  return typeof color === "string" && /^#[0-9a-fA-F]{6}$/.test(color)
    ? color
    : FALLBACK_AWARENESS_COLOR;
}

function snapshotCanvasAwareness(
  awareness: CanvasAwarenessHandle | null | undefined,
): readonly CanvasAwarenessPeer[] {
  if (!awareness) return [];
  const out: CanvasAwarenessPeer[] = [];
  awareness.getStates().forEach((value, clientId) => {
    const state = normalizeAwarenessState(value);
    if (state) out.push({ clientId, state });
  });
  return out;
}

function snapshotCanvasWidgetActivity(
  awareness: CanvasAwarenessHandle | null | undefined,
): {
  readonly peers: readonly CanvasAwarenessPeer[];
  readonly signature: string;
} {
  if (!awareness) return { peers: [], signature: "" };
  const peers: CanvasAwarenessPeer[] = [];
  const signatureParts: string[] = [];
  awareness.getStates().forEach((value, clientId) => {
    const state = normalizeAwarenessState(value);
    if (!state) return;
    const activeWidget = activeWidgetFromAwarenessState(state);
    if (!activeWidget) return;
    const activityState: CanvasAwarenessFields = {
      ...(state.user ? { user: state.user } : {}),
      ...(state.selection !== undefined ? { selection: state.selection } : {}),
      activeWidget,
    };
    peers.push({ clientId, state: activityState });
    signatureParts.push(
      [
        clientId,
        state.user?.id ?? "",
        state.user?.name ?? "",
        state.user?.color ?? "",
        state.selection ?? "",
        activeWidget.id,
        activeWidget.mode,
      ].join(":"),
    );
  });
  return { peers, signature: signatureParts.join("|") };
}

function activeWidgetFromAwarenessState(
  state: CanvasAwarenessFields,
): CanvasAwarenessActiveWidget | null {
  if (state.activeWidget !== undefined) return state.activeWidget;
  return state.selection ? { id: state.selection, mode: "selected" } : null;
}

function normalizeAwarenessState(value: unknown): CanvasAwarenessFields | null {
  if (!isRecord(value)) return null;
  const user = normalizeUser(value["user"]);
  const cursor =
    "cursor" in value ? normalizeCursor(value["cursor"]) : undefined;
  const selection =
    "selection" in value ? normalizeSelection(value["selection"]) : undefined;
  const facilitator = value["facilitator"] === true ? true : undefined;
  const activeWidget =
    "activeWidget" in value
      ? normalizeActiveWidget(value["activeWidget"])
      : undefined;
  const state: CanvasAwarenessFields = {
    ...(user ? { user } : {}),
    ...(cursor !== undefined ? { cursor } : {}),
    ...(selection !== undefined ? { selection } : {}),
    ...(facilitator !== undefined ? { facilitator } : {}),
    ...(activeWidget !== undefined ? { activeWidget } : {}),
  };
  return state;
}

function normalizeUser(value: unknown): CanvasAwarenessUser | null {
  if (!isRecord(value)) return null;
  const id = value["id"];
  const name = value["name"];
  const color = value["color"];
  if (typeof id !== "string" || id.length === 0) return null;
  return {
    id,
    name: typeof name === "string" && name.length > 0 ? name : id,
    color: typeof color === "string" ? color : "",
  };
}

function normalizeCursor(value: unknown): CanvasAwarenessFields["cursor"] {
  if (value === null) return null;
  if (!isRecord(value)) return undefined;
  const x = value["x"];
  const y = value["y"];
  if (
    typeof x !== "number" ||
    typeof y !== "number" ||
    !Number.isFinite(x) ||
    !Number.isFinite(y)
  ) {
    return undefined;
  }
  return { x, y };
}

function normalizeSelection(
  value: unknown,
): CanvasAwarenessFields["selection"] {
  if (value === null) return null;
  return typeof value === "string" ? value : undefined;
}

function normalizeActiveWidget(
  value: unknown,
): CanvasAwarenessActiveWidget | null | undefined {
  if (value === null) return null;
  if (!isRecord(value)) return undefined;
  const id = value["id"];
  const mode = value["mode"];
  if (typeof id !== "string" || id.length === 0) return undefined;
  if (!isActiveWidgetMode(mode)) return undefined;
  return { id, mode };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isActiveWidgetMode(
  value: unknown,
): value is CanvasAwarenessActiveWidget["mode"] {
  return typeof value === "string" && ACTIVE_WIDGET_MODES.has(value as never);
}
